/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { generateUrl } from '../../routes/util';
import { CustomReport, CustomReportProps, CustomReportType, NewMetrics } from '../../types/custom-report';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import { FormGenerator, FieldProps } from '@g17eco/molecules/form';
import { CustomReportRoutes } from './CustomReportRoutes';
import './styles.scss'
import { useCreateCustomReportMutation, useUpdateCustomReportMutation } from '@api/custom-reports';

const fields: FieldProps[] = [
  {
    code: 'name',
    type: 'text',
    label: 'Name of custom report',
    placeholder: `For example: Gender diversity report for CSR ${(new Date()).getFullYear()}`,
    required: true,
  },
  {
    code: 'description',
    type: 'textarea',
    label: 'Description',
    placeholder: '',
  },
];

interface FormData {
  _id?: string;
  name?: string;
  description?: string;
}

const getInitialState = (customReport?: CustomReport): FormData => {
  return {
    _id: customReport?._id,
    name: customReport?.name,
    description: customReport?.description,
  }
};


const CustomReportEditForm = ({ customReport, initiativeId }: CustomReportProps) => {

  const [formData, setFormData] = useState<FormData | undefined>(getInitialState(customReport));

  const history = useHistory();

  const [createCustomReport, { isLoading: isCreating }] = useCreateCustomReportMutation();
  const [updateCustomReport, { isLoading: isUpdating }] = useUpdateCustomReportMutation();
  const isSaving = isCreating || isUpdating;

  React.useEffect(() => {
    setFormData(getInitialState(customReport));
  }, [customReport]);

  const isFormValid = Boolean(formData?.name);

  const handleUpdate = (e: React.ChangeEvent<{ name: string, value: string | number | undefined }>) => {
    if (!fields.map(f => f.code).includes(e.target.name)) {
      return;
    }

    setFormData({
      ...(formData ?? {}),
      [e.target.name]: e.target.value
    });
  };

  const isEditMode = Boolean(formData?._id);

  const handleSubmit = async () => {
    if (!formData || !formData.name) {
      return;
    }

    const data: NewMetrics = {
      type: CustomReportType.Metrics,
      name: formData.name,
      description: formData?.description
    }

    const method = formData._id ? updateCustomReport({initiativeId, reportId: formData._id, formData: data}) : createCustomReport({initiativeId, formData: data});
    method
      .unwrap()
      .then((data) => {
        const url = generateUrl(CustomReportRoutes.MANAGE_REPORT, { initiativeId, reportId: data._id });
        setFormData(undefined);
        history.push(url);
      });
  }

  return <>
    <FormGenerator
      form={formData ?? {}}
      fields={fields.map(f => ({ ...f, disabled: isSaving }))}
      updateForm={handleUpdate}
    />
    <div className='mt-4 text-right'>
      <SubmitButton
        disabled={!isFormValid || isSaving}
        color='primary'
        onClick={handleSubmit}>
        {isEditMode ? 'Update' : 'Create'}
      </SubmitButton>
    </div>
  </>
}

export default CustomReportEditForm;
